// @ts-nocheck

import axiosLib from "axios";
import {
  notify,
  NotificationType,
  findNotificationConfigByUrl,
} from "@moonscape/shared";

// Import trackEvent for web analytics (only available on web)
let trackEvent: ((eventName: string, properties: any) => void) | null = null;
let ERROR_RESILIENCE_EVENTS: any = null;

// Try to import web analytics if available (web-only)
if (typeof window !== "undefined") {
  try {
    // Dynamic import for web only - will fail gracefully on mobile
    const analyticsModule = require("../../../web/src/analytics");
    const eventsModule = require("../../../web/src/analytics/events");
    trackEvent = analyticsModule?.trackEvent;
    ERROR_RESILIENCE_EVENTS = eventsModule?.ERROR_RESILIENCE_EVENTS;
  } catch (e) {
    // Not on web platform, analytics not available
  }
}

/** instance for protected/authenticated endpoints */
export const axios = axiosLib.create({
  baseURL: process.env.REACT_APP_SERVER_URL,
});

// Accountant context token — set when CPA is viewing a client
let accountantContextToken: string | null = null;
let onAccountantContextInvalid: (() => void) | null = null;

/** Set or clear the accountant context token for API calls */
export const setAccountantContextToken = (token: string | null) => {
  accountantContextToken = token;
};

/** Set callback for invalid/expired accountant context token */
export const setOnAccountantContextInvalid = (callback: (() => void) | null) => {
  onAccountantContextInvalid = callback;
};

const isAccountantControlRoute = (url: string = "") => {
  return /^\/api\/accountant(?:-invites)?(?:\/|$)/.test(url)
    || /^\/accountant(?:-invites)?(?:\/|$)/.test(url);
};

const isAccountantContextAuthError = (error: any) => {
  const status = error?.response?.status;
  const message = String(error?.response?.data?.error || "").toLowerCase();
  const hadContextHeader = !!error?.config?.headers?.["X-Accountant-Context"];

  if (!hadContextHeader || ![401, 403].includes(status)) {
    return false;
  }

  return (
    message.includes("context token")
    || message.includes("invalid context")
    || message.includes("client user not found")
    || message.includes("no access to this client")
  );
};

// Request interceptor: attach accountant context header when viewing a client
axios.interceptors.request.use((config) => {
  if (accountantContextToken && !isAccountantControlRoute(config.url || "")) {
    config.headers["X-Accountant-Context"] = accountantContextToken;
  }
  return config;
});

// Callback for handling 401 errors - set from App.js
let onUnauthorized: (() => void) | null = null;
// Debounce: a burst of parallel 401s must trigger one login redirect, not many
let unauthorizedFiredAt = 0;

/** Set callback to handle 401 unauthorized responses (triggers login redirect) */
export const setOnUnauthorized = (callback: () => void) => {
  onUnauthorized = callback;
};

axios.interceptors.response.use(
  (response) => {
    const res = response.data;
    const enableConfig = findNotificationConfigByUrl(
      response.config.url?.split("?")[0]
    );
    if (
      !enableConfig ||
      !enableConfig.allowMethod.includes(response.config.method?.toUpperCase())
    ) {
      return response;
    }
    const { add } = notify();

    if ([200, 201, 204, 304].includes(response.status)) {
      add({
        type: NotificationType.SUCCESS,
        alertText: res.msg || enableConfig.successMsg,
      });
    } else {
      add({
        type: NotificationType.ERROR,
        alertText:
          res.msg ||
          enableConfig.errorMsg ||
          "An error occurred, Please try again later",
      });
    }

    return response;
  },
  (error) => {
    const enableConfig = findNotificationConfigByUrl(
      error.config.url?.split("?")[0]
    );

    if (isAccountantContextAuthError(error) && onAccountantContextInvalid) {
      onAccountantContextInvalid();
    }

    // Handle 401 unauthorized - trigger login redirect
    if (error.response?.status === 401 && onUnauthorized) {
      const now = Date.now();
      if (now - unauthorizedFiredAt > 5000) {
        unauthorizedFiredAt = now;
        console.log("[axios] 401 Unauthorized - triggering login redirect");
        onUnauthorized();
      }
      return Promise.reject(error);
    }

    // Track API errors (web only)
    if (trackEvent && ERROR_RESILIENCE_EVENTS) {
      try {
        const isSyncError = error.config?.url?.includes("/sync") || error.config?.url?.includes("/exchange");

        const errorEvent = isSyncError
          ? ERROR_RESILIENCE_EVENTS.SYNC_FAILED
          : ERROR_RESILIENCE_EVENTS.API_REQUEST_FAILED;

        trackEvent(errorEvent, {
          error_type: error.response?.status >= 500 ? "server_error" : "client_error",
          error_code: error.response?.status || "network_error",
          error_message: error.response?.data?.msg || error.message || "Unknown error",
          endpoint: error.config?.url,
          http_status: error.response?.status,
          http_method: error.config?.method?.toUpperCase(),
          source: "web",
        });
      } catch (e) {
        // Silent fail if tracking fails
      }
    }

    if (
      !enableConfig ||
      !enableConfig.allowMethod.includes(error.config.method?.toUpperCase())
    ) {
      return Promise.reject(error);
    }
    const { add } = notify();
    add({
      type: NotificationType.ERROR,
      alertText:
        error.response?.data?.msg || enableConfig.errorMsg ||
        "Server connection error, Please try again later",
    });
    return Promise.reject(error);
  }
);

/** function to set the instances headers when we get the access token */
export const setAuthToken = (token) => {
  console.log("[axios] set access token");
  if (token) {
    //applying token
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    //deleting the token from header
    delete axios.defaults.headers.common["Authorization"];
  }
};

/** sets the header in the components after calling getAccessTokenSilently() */
export const authorizedAxios = (token) => {
  // Never poison the shared default header with "Bearer undefined" — a single
  // failed token fetch would break every subsequent request app-wide.
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }
  return axios;
};

export default axios;
